import React from 'react';

type SortDir = 'asc' | 'desc';

/** Sort control for use inside a custom `<th>` (e.g. next to filter buttons). */
export function SortHeaderButton({
  label,
  columnKey,
  sortKey,
  sortDir,
  onSort,
  className = '',
}: {
  label: React.ReactNode;
  columnKey: string;
  sortKey: string | null;
  sortDir: SortDir;
  onSort: (key: string) => void;
  className?: string;
}) {
  const active = sortKey === columnKey;
  return (
    <button
      type="button"
      className={`sortable-th-btn${active ? ' sortable-th-btn--active' : ''} ${className}`.trim()}
      onClick={() => onSort(columnKey)}
    >
      <span className="sortable-th-label">{label}</span>
      <span className="sortable-th-icon" aria-hidden>
        {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
      </span>
    </button>
  );
}

export function SortableTh({
  label,
  columnKey,
  sortKey,
  sortDir,
  onSort,
  className = '',
}: {
  label: React.ReactNode;
  columnKey: string;
  sortKey: string | null;
  sortDir: SortDir;
  onSort: (key: string) => void;
  className?: string;
}) {
  const active = sortKey === columnKey;
  return (
    <th
      scope="col"
      className={`sortable-th ${className}`.trim()}
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className={`sortable-th-btn${active ? ' sortable-th-btn--active' : ''}`}
        onClick={() => onSort(columnKey)}
      >
        <span className="sortable-th-label">{label}</span>
        <span className="sortable-th-icon" aria-hidden>
          {active ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
        </span>
      </button>
    </th>
  );
}

/** Header cell that is not sortable (e.g. Actions). */
export function StaticTh({
  label,
  className = '',
  children,
  ...rest
}: React.ThHTMLAttributes<HTMLTableCellElement> & { label?: React.ReactNode }) {
  return (
    <th scope="col" className={`sortable-th sortable-th--static ${className}`.trim()} {...rest}>
      {children ?? label}
    </th>
  );
}
